'use client';

import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useUnreadCount as useMessagesUnreadCount } from '@/lib/hooks/useMessages';
import { useUnreadCount as useNotificationsUnreadCount } from '@/lib/hooks/useNotifications';
import { cssVars } from '@/styles/theme';
import { motion } from 'framer-motion';
import {
  Bell,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  LayoutDashboard,
  List,
  LogOut,
  MessageSquare,
  Package,
  Settings,
  ShoppingBag,
  TrendingUp,
  User,
} from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface NavItemType {
  id: string;
  icon: typeof LayoutDashboard;
  labelKey: string;
  href: string;
  badge?: number;
}

interface DashboardSidebarProps {
  userType: 'client' | 'supplier';
}

// Memoized NavItem component to prevent re-renders on pathname change
const NavItem = memo(
  ({
    item,
    active,
    isCollapsed,
    isDark,
    isRTL,
    shouldStayCollapsedRef,
    t,
    Icon,
  }: {
    item: NavItemType;
    active: boolean;
    isCollapsed: boolean;
    isDark: boolean;
    isRTL: boolean;
    shouldStayCollapsedRef: React.MutableRefObject<boolean>;
    t: (key: string) => string;
    Icon: typeof LayoutDashboard;
  }) => {
    // Memoize label to prevent re-translation on every render
    const label = useMemo(() => t(item.labelKey), [t, item.labelKey]);
    return (
      <Link
        href={item.href}
        title={isCollapsed ? label : undefined}
        onClick={(e) => {
          if (isCollapsed) {
            shouldStayCollapsedRef.current = true;
          }
        }}
        onMouseEnter={(e) => {
          if (isCollapsed) {
            e.stopPropagation();
            shouldStayCollapsedRef.current = true;
          }
        }}
        onFocus={(e) => {
          if (isCollapsed) {
            e.stopPropagation();
            shouldStayCollapsedRef.current = true;
          }
        }}
      >
        <motion.div
          whileHover={!isCollapsed ? { scale: 1.02, x: isRTL ? -2 : 2 } : { scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          className={`relative flex min-h-[44px] touch-manipulation items-center rounded-xl px-3 py-3 transition-all sm:px-4 sm:py-3 ${
            active ? 'shadow-lg' : ''
          } ${isCollapsed ? 'justify-center px-2' : 'justify-between'}`}
          style={{
            backgroundColor: active
              ? isDark
                ? `color-mix(in srgb, ${cssVars.accent.warm} 20%, transparent)`
                : `color-mix(in srgb, ${cssVars.accent.warm} 10%, transparent)`
              : 'transparent',
            color: active
              ? cssVars.accent.warm
              : isDark
                ? cssVars.neutral.textMuted
                : cssVars.neutral.bg,
            maxWidth: isCollapsed ? '100%' : 'none',
            overflow: isCollapsed ? 'hidden' : 'visible',
          }}
        >
          <div className="relative flex items-center gap-2 sm:gap-3">
            <Icon className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5" />
            {item.badge && isCollapsed && (
              <span
                className={`absolute ${isRTL ? '-left-1' : '-right-1'} -top-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold`}
                style={{
                  backgroundColor: cssVars.accent.primary,
                  color: isDark ? cssVars.neutral.surface : cssVars.secondary.DEFAULT,
                }}
                aria-label={`${item.badge} ${label}`}
              >
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            )}
            {!isCollapsed && (
              <span className="whitespace-nowrap text-sm font-semibold sm:text-base">{label}</span>
            )}
          </div>
          {item.badge && !isCollapsed && (
            <span
              className="rounded-full px-1.5 py-0.5 text-xs font-bold sm:px-2"
              style={{
                backgroundColor: cssVars.accent.primary,
                color: isDark ? cssVars.neutral.surface : cssVars.secondary.DEFAULT,
              }}
            >
              {item.badge}
            </span>
          )}
        </motion.div>
      </Link>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if active state, collapsed state, or item changes
    return (
      prevProps.active === nextProps.active &&
      prevProps.isCollapsed === nextProps.isCollapsed &&
      prevProps.item.id === nextProps.item.id &&
      prevProps.item.badge === nextProps.item.badge &&
      prevProps.isDark === nextProps.isDark &&
      prevProps.isRTL === nextProps.isRTL
    );
  }
);

NavItem.displayName = 'NavItem';

function DashboardSidebar({ userType }: DashboardSidebarProps) {
  const t = useTranslations('nav');
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const { logout } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isRTL = locale === 'ar';

  // Real counts from backend
  const { data: unreadNotificationsCount } = useNotificationsUnreadCount();
  const { data: unreadMessagesCount } = useMessagesUnreadCount();

  // State for sidebar collapse
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  // Ref to track if sidebar should stay collapsed (prevents auto-expansion)
  const shouldStayCollapsedRef = useRef(false);

  // Load collapsed state from localStorage on mount
  useEffect(() => {
    setMounted(true);
    const savedState = localStorage.getItem('sidebar-collapsed');
    if (savedState !== null) {
      const savedCollapsed = JSON.parse(savedState);
      setIsCollapsed(savedCollapsed);
      shouldStayCollapsedRef.current = savedCollapsed;
    }
  }, []);

  // Save collapsed state to localStorage
  useEffect(() => {
    if (mounted) {
      localStorage.setItem('sidebar-collapsed', JSON.stringify(isCollapsed));
      shouldStayCollapsedRef.current = isCollapsed;
    }
  }, [isCollapsed, mounted]);

  const toggleSidebar = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    // Only way to change sidebar state - update both state and ref
    setIsCollapsed((prev) => {
      const newState = !prev;
      shouldStayCollapsedRef.current = newState;
      return newState;
    });
  }, []);

  const sidebarWidth = useMemo(() => (isCollapsed ? 80 : 280), [isCollapsed]);

  // Update CSS variable for sidebar width to sync with Header
  useEffect(() => {
    if (mounted) {
      document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
    }
  }, [sidebarWidth, mounted]);

  const clientNavItems: NavItemType[] = useMemo(
    () => [
      { id: 'dashboard', icon: LayoutDashboard, labelKey: 'dashboard', href: '/dashboard' },
      { id: 'requests', icon: FileText, labelKey: 'requests', href: '/requests' },
      { id: 'projects', icon: Briefcase, labelKey: 'projects', href: '/projects' },
      { id: 'contracts', icon: FileText, labelKey: 'contracts', href: '/contracts' },
      { id: 'orders', icon: FileText, labelKey: 'orders', href: '/orders' },
      { id: 'payments', icon: CreditCard, labelKey: 'payments', href: '/payments' },
      {
        id: 'messages',
        icon: MessageSquare,
        labelKey: 'messages',
        href: '/messages',
        badge: unreadMessagesCount && unreadMessagesCount > 0 ? unreadMessagesCount : undefined,
      },
      {
        id: 'notifications',
        icon: Bell,
        labelKey: 'notifications',
        href: '/notifications',
        badge:
          unreadNotificationsCount && unreadNotificationsCount > 0
            ? unreadNotificationsCount
            : undefined,
      },
      { id: 'profile', icon: User, labelKey: 'profile', href: '/profile' },
      { id: 'settings', icon: Settings, labelKey: 'settings', href: '/profile/settings' },
    ],
    [unreadMessagesCount, unreadNotificationsCount]
  );

  const supplierNavItems: NavItemType[] = useMemo(
    () => [
      { id: 'dashboard', icon: LayoutDashboard, labelKey: 'dashboard', href: '/dashboard' },
      {
        id: 'browse-requests',
        icon: ShoppingBag,
        labelKey: 'browseRequests',
        href: '/supplier/browse-requests',
      },
      { id: 'offers', icon: Briefcase, labelKey: 'offers', href: '/offers' },
      { id: 'projects', icon: Package, labelKey: 'projects', href: '/supplier-projects' },
      { id: 'contracts', icon: FileText, labelKey: 'contracts', href: '/contracts' },
      {
        id: 'payments',
        icon: CreditCard,
        labelKey: 'payments',
        href: '/payments/financial',
      },
      { id: 'menu', icon: List, labelKey: 'myMenu', href: '/supplier/menu' },
      { id: 'supplier-orders', icon: FileText, labelKey: 'productOrders', href: '/supplier/orders' },
      { id: 'stats', icon: TrendingUp, labelKey: 'stats', href: '/stats' },
      {
        id: 'messages',
        icon: MessageSquare,
        labelKey: 'messages',
        href: '/messages',
        badge: unreadMessagesCount && unreadMessagesCount > 0 ? unreadMessagesCount : undefined,
      },
      { id: 'profile', icon: User, labelKey: 'profile', href: '/profile' },
      { id: 'settings', icon: Settings, labelKey: 'settings', href: '/profile/settings' },
    ],
    [unreadMessagesCount]
  );

  const navItems = useMemo(
    () => (userType === 'client' ? clientNavItems : supplierNavItems),
    [userType, clientNavItems, supplierNavItems]
  );

  // Bottom bar: show first 4 items (client) or 5 (supplier, to include payments/payout); rest go in "More" sheet
  const bottomBarVisibleCount = userType === 'supplier' ? 5 : 4;
  const bottomBarItems = useMemo(
    () => navItems.slice(0, bottomBarVisibleCount),
    [navItems, bottomBarVisibleCount]
  );
  const moreSheetItems = useMemo(
    () => navItems.slice(bottomBarVisibleCount),
    [navItems, bottomBarVisibleCount]
  );
  const hasMoreItems = moreSheetItems.length > 0;

  // Memoize pathname processing to avoid recalculation
  const pathWithoutLocale = useMemo(() => {
    if (!pathname) return '/';
    return pathname.replace(`/${locale}`, '') || '/';
  }, [pathname, locale]);

  // Mapping for sub-routes that should activate parent menu items
  // e.g., /supplier-profile/setup should activate /profile
  const subRouteMapping = useMemo(
    () => ({
      '/supplier-profile': '/profile',
      '/supplier-profile/setup': '/profile',
      '/supplier-profile/edit': '/profile',
      '/supplier-profile/verification-status': '/profile',
      '/profile/edit': '/profile',
      '/profile/account': '/profile',
      '/profile/portfolio': '/profile',
      '/profile/setup': '/profile',
      '/profile/setup/documents': '/profile',
      '/profile/verification': '/profile',
      '/payments/withdrawals': '/payments/financial',
      '/payments/wallet': '/payments/financial',
    }),
    []
  );

  // Get all hrefs from navItems to check for exact matches first
  const allNavHrefs = useMemo(
    () => navItems.map((item) => item.href.replace(`/${locale}`, '') || '/'),
    [navItems, locale]
  );

  // Paths that should only match exactly (no sub-routes)
  // e.g., /profile should NOT match /profile/settings
  const exactOnlyPaths = useMemo(
    () => ['/dashboard', '/profile', '/settings', '/supplier/settings', '/profile/settings'],
    []
  );

  // Improved isActive function to prevent multiple items being active at once
  // Matches the logic used in Header component
  // Memoized to prevent recalculation on every render
  const isActive = useCallback(
    (href: string): boolean => {
      if (!pathname) return false;

      const hrefWithoutLocale = href.replace(`/${locale}`, '') || '/';

      // First, check for exact matches (highest priority)
      if (pathWithoutLocale === hrefWithoutLocale) {
        return true;
      }

      // Check if current path matches a sub-route mapping
      // If path is /supplier-profile/setup and href is /profile, it should be active
      // This check happens BEFORE exactOnlyPaths to allow sub-routes to work
      const mappedPath = subRouteMapping[pathWithoutLocale as keyof typeof subRouteMapping];
      if (mappedPath && mappedPath === hrefWithoutLocale) {
        return true;
      }

      // Also check if path starts with a mapped parent route
      // e.g., /supplier-profile/setup should match /supplier-profile -> /profile
      for (const [subRoute, parentRoute] of Object.entries(subRouteMapping)) {
        if (pathWithoutLocale.startsWith(subRoute) && parentRoute === hrefWithoutLocale) {
          // Make sure this sub-route doesn't match a more specific nav item
          // e.g., /profile/settings should match /profile/settings, not /profile
          const hasMoreSpecificMatch = allNavHrefs.some(
            (navHref) =>
              navHref !== hrefWithoutLocale &&
              pathWithoutLocale.startsWith(navHref) &&
              navHref.length > hrefWithoutLocale.length
          );
          if (!hasMoreSpecificMatch) {
            return true;
          }
        }
      }

      // If this href should only match exactly (and no sub-route mapping matched),
      // don't check for other sub-routes
      if (exactOnlyPaths.includes(hrefWithoutLocale)) {
        return false;
      }

      // For other paths, check if pathname starts with href (for nested routes)
      // But ensure it's not a false positive (e.g., /messages shouldn't match /messages/new)
      if (pathWithoutLocale.startsWith(hrefWithoutLocale)) {
        // Additional check: ensure the next character is / or end of string
        const nextChar = pathWithoutLocale[hrefWithoutLocale.length];
        if (nextChar === '/' || nextChar === undefined) {
          // Make sure there's no more specific nav item that matches better
          // e.g., /profile/settings should match /profile/settings, not /profile
          const hasMoreSpecificMatch = allNavHrefs.some(
            (navHref) =>
              navHref !== hrefWithoutLocale &&
              pathWithoutLocale.startsWith(navHref) &&
              navHref.length > hrefWithoutLocale.length
          );
          return !hasMoreSpecificMatch;
        }
      }

      return false;
    },
    [pathWithoutLocale, pathname, locale, subRouteMapping, allNavHrefs, exactOnlyPaths]
  );

  const handleLogout = async () => {
    try {
      await logout();
      router.push(`/${locale}/login`);
    } catch (error) {
      console.error('Logout error:', error);
      // Still redirect to login even if logout fails
      router.push(`/${locale}/login`);
    }
  };

  return (
    <>
      {/* Desktop Sidebar - Always visible */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarWidth }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`fixed top-0 hidden h-screen overflow-hidden border-b backdrop-blur-lg lg:block ${
          isRTL ? 'right-0 border-l' : 'left-0 border-r'
        }`}
        style={{
          backgroundColor: isDark
            ? `color-mix(in srgb, ${cssVars.neutral.background} 85%, transparent)`
            : `color-mix(in srgb, ${cssVars.primary.DEFAULT} 95%, transparent)`,
          borderColor: isDark
            ? `color-mix(in srgb, ${cssVars.neutral.border} 30%, transparent)`
            : `color-mix(in srgb, ${cssVars.neutral.border} 20%, transparent)`,
          zIndex: 1000,
          willChange: 'width',
          minWidth: `${sidebarWidth}px`,
          maxWidth: `${sidebarWidth}px`,
        }}
        onMouseEnter={() => {
          // Prevent any automatic expansion on hover
          if (isCollapsed) {
            shouldStayCollapsedRef.current = true;
          }
        }}
        onMouseLeave={() => {
          // Keep collapsed state when mouse leaves
          if (isCollapsed) {
            shouldStayCollapsedRef.current = true;
          }
        }}
      >
        <div className="flex h-full flex-col overflow-y-auto">
          {/* Sidebar Header - Logo/Brand with Toggle */}
          <div
            className={`flex items-center border-b px-4 py-6 ${
              isCollapsed ? 'justify-center' : 'justify-between'
            }`}
            style={{
              borderColor: isDark
                ? `color-mix(in srgb, ${cssVars.neutral.border} 30%, transparent)`
                : `color-mix(in srgb, ${cssVars.neutral.border} 20%, transparent)`,
            }}
          >
            {isCollapsed ? (
              <Link href="/" aria-label={t('home')}></Link>
            ) : (
              <Link href="/" aria-label={t('home')}></Link>
            )}
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleSidebar(e);
              }}
              className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl transition-all ${
                !isCollapsed ? 'ml-auto' : ''
              }`}
              style={{
                backgroundColor: `color-mix(in srgb, ${cssVars.neutral.textMuted} 15%, transparent)`,
                color: isDark ? cssVars.neutral.textMuted : cssVars.neutral.bg,
              }}
              aria-label={
                isCollapsed
                  ? t('expandSidebar') || 'Expand sidebar'
                  : t('collapseSidebar') || 'Collapse sidebar'
              }
            >
              {isRTL ? (
                isCollapsed ? (
                  <ChevronLeft className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )
              ) : isCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </motion.button>
          </div>

          <nav className="space-y-1 p-3 sm:p-4">
            {navItems.map((item) => {
              const active = isActive(item.href);
              const Icon = item.icon;
              return (
                <NavItem
                  key={item.id}
                  item={item}
                  active={active}
                  isCollapsed={isCollapsed}
                  isDark={isDark}
                  isRTL={isRTL}
                  shouldStayCollapsedRef={shouldStayCollapsedRef}
                  t={t}
                  Icon={Icon}
                />
              );
            })}

            {/* Divider */}
            <div
              className="my-4 h-px"
              style={{
                backgroundColor: isDark
                  ? `color-mix(in srgb, ${cssVars.neutral.border} 30%, transparent)`
                  : `color-mix(in srgb, ${cssVars.neutral.border} 20%, transparent)`,
              }}
            />

            <motion.button
              whileHover={!isCollapsed ? { scale: 1.02, x: isRTL ? -2 : 2 } : { scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={(e) => {
                // Prevent sidebar from expanding when clicking logout in collapsed state
                if (isCollapsed) {
                  e.stopPropagation();
                }
                handleLogout();
              }}
              className={`flex min-h-[44px] w-full touch-manipulation items-center gap-2 rounded-xl px-3 py-3 transition-all sm:gap-3 sm:px-4 sm:py-3 ${
                isCollapsed ? 'justify-center' : ''
              }`}
              style={{
                backgroundColor: `color-mix(in srgb, ${cssVars.status.error} 10%, transparent)`,
                color: cssVars.status.error,
              }}
              title={isCollapsed ? t('logout') : undefined}
            >
              <LogOut className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5" />
              {!isCollapsed && (
                <span className="whitespace-nowrap text-sm font-semibold sm:text-base">
                  {t('logout')}
                </span>
              )}
            </motion.button>
          </nav>
        </div>
      </motion.aside>

      {/* Mobile Bottom Navigator Bar */}
      <nav
        className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-around border-t backdrop-blur-lg lg:hidden"
        style={{
          backgroundColor: isDark
            ? `color-mix(in srgb, ${cssVars.neutral.background} 85%, transparent)`
            : `color-mix(in srgb, ${cssVars.primary.DEFAULT} 95%, transparent)`,
          borderColor: isDark
            ? `color-mix(in srgb, ${cssVars.neutral.border} 30%, transparent)`
            : `color-mix(in srgb, ${cssVars.neutral.border} 20%, transparent)`,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        {/* First 4 items in bottom bar */}
        {bottomBarItems.map((item) => {
          const active = isActive(item.href);
          return (
            <Link key={item.id} href={item.href} className="flex-1">
              <motion.div
                whileTap={{ scale: 0.95 }}
                className={`flex min-h-[64px] touch-manipulation flex-col items-center justify-center gap-1 transition-all ${
                  active ? 'shadow-sm' : ''
                }`}
                style={{
                  backgroundColor: active
                    ? isDark
                      ? `color-mix(in srgb, ${cssVars.accent.warm} 20%, transparent)`
                      : `color-mix(in srgb, ${cssVars.accent.warm} 10%, transparent)`
                    : 'transparent',
                  color: active
                    ? cssVars.accent.warm
                    : isDark
                      ? cssVars.neutral.textMuted
                      : cssVars.neutral.bg,
                }}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.badge && (
                    <span
                      className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs font-bold"
                      style={{
                        backgroundColor: cssVars.accent.primary,
                        color: isDark ? cssVars.neutral.surface : cssVars.secondary.DEFAULT,
                      }}
                    >
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className="max-w-full truncate px-1 text-xs font-medium sm:text-sm">
                  {t(item.labelKey)}
                </span>
              </motion.div>
            </Link>
          );
        })}

        {/* More: opens sheet with Messages, Notifications, Profile, Settings, etc. */}
        {hasMoreItems && (
          <>
            <button
              type="button"
              onClick={() => setShowMoreSheet(true)}
              className="flex min-h-[64px] touch-manipulation flex-1 flex-col items-center justify-center gap-1 transition-all"
              style={{
                color: isDark ? cssVars.neutral.textMuted : cssVars.neutral.bg,
              }}
              aria-label={t('more')}
            >
              <div className="relative flex h-5 w-5 items-center justify-center">
                <div className="absolute left-0 top-1/2 h-1 w-1 -translate-x-1 -translate-y-1/2 rounded-full bg-current" />
                <div className="h-1 w-1 rounded-full bg-current" />
                <div className="absolute right-0 top-1/2 h-1 w-1 -translate-y-1/2 translate-x-1 rounded-full bg-current" />
              </div>
              <span className="max-w-full truncate px-1 text-xs font-medium sm:text-sm" style={{ maxWidth: '80px' }}>
                {t('more')}
              </span>
            </button>

            {/* More sheet overlay - click on empty area to close */}
            {showMoreSheet && (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  aria-label={t('closeMenu')}
                  className="fixed inset-0 z-[59] cursor-default bg-black/50 backdrop-blur-sm"
                  onClick={() => setShowMoreSheet(false)}
                  onKeyDown={(e) => e.key === 'Escape' && setShowMoreSheet(false)}
                />
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'tween', duration: 0.25 }}
                  className="fixed inset-x-0 bottom-0 z-[60] rounded-t-2xl border-t-2 pb-[env(safe-area-inset-bottom)]"
                  style={{
                    backgroundColor: isDark
                      ? `color-mix(in srgb, ${cssVars.neutral.background} 98%, transparent)`
                      : `color-mix(in srgb, ${cssVars.primary.DEFAULT} 98%, transparent)`,
                    borderColor: cssVars.neutral.border,
                    maxHeight: '70vh',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex min-h-[44px] items-center justify-center border-b py-2" style={{ borderColor: cssVars.neutral.border }}>
                    <span className="text-sm font-bold" style={{ color: cssVars.secondary.DEFAULT }}>{t('more')}</span>
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto p-3">
                    {moreSheetItems.map((item) => {
                      const active = isActive(item.href);
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.id}
                          href={item.href}
                          onClick={() => setShowMoreSheet(false)}
                          className="flex min-h-[44px] touch-manipulation items-center gap-3 rounded-xl px-4 py-3"
                          style={{
                            backgroundColor: active
                              ? `color-mix(in srgb, ${cssVars.accent.warm} 15%, transparent)`
                              : 'transparent',
                            color: active ? cssVars.accent.warm : (isDark ? cssVars.neutral.darker : cssVars.neutral.bg),
                          }}
                        >
                          <Icon className="h-5 w-5 flex-shrink-0" />
                          <span className="text-sm font-semibold">{t(item.labelKey)}</span>
                          {item.badge != null && item.badge > 0 && (
                            <span
                              className="ms-auto rounded-full px-2 py-0.5 text-xs font-bold"
                              style={{
                                backgroundColor: cssVars.accent.primary,
                                color: isDark ? cssVars.neutral.surface : cssVars.secondary.DEFAULT,
                              }}
                            >
                              {item.badge > 9 ? '9+' : item.badge}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                    {/* Logout inside More sheet on mobile - not in bottom bar */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowMoreSheet(false);
                        handleLogout();
                      }}
                      className="flex min-h-[44px] w-full touch-manipulation items-center gap-3 rounded-xl px-4 py-3 mt-2"
                      style={{
                        backgroundColor: `color-mix(in srgb, ${cssVars.status.error} 10%, transparent)`,
                        color: cssVars.status.error,
                      }}
                    >
                      <LogOut className="h-5 w-5 flex-shrink-0" />
                      <span className="text-sm font-semibold">{t('logout')}</span>
                    </button>
                  </div>
                </motion.div>
              </>
            )}
          </>
        )}
      </nav>
    </>
  );
}

// Memoize with comparison to prevent re-renders unless userType changes
export default memo(DashboardSidebar, (prevProps, nextProps) => {
  return prevProps.userType === nextProps.userType;
});
